import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_96339eda-de9f-4afd-b0cc-aa940ad2aaf2/artifacts/doc4booq_SubGuard_icon.png" 
                  alt="SubGuard" 
                  className="w-8 h-8 rounded-lg"
                />
                <span className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                  SubGuard
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
          Terms of Service
        </h1>
        <p className="text-gray-500 mb-8">Last updated: January 2026</p>

        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 mb-4">
              By accessing or using SubGuard ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
            </p>
            <p className="text-gray-600">
              SubGuard is operated by SubGuard ("we," "us," or "our"). We reserve the right to update these Terms at any time, and your continued use of the Service constitutes acceptance of any changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-600 mb-4">
              SubGuard is a subscription management platform that helps users:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Track and manage their recurring subscriptions</li>
              <li>Detect subscriptions from email receipts (with Gmail integration)</li>
              <li>Receive renewal alerts and notifications</li>
              <li>Get AI-powered assistance for cancelling subscriptions</li>
              <li>Track savings from cancelled subscriptions</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Account Registration</h2>
            <p className="text-gray-600 mb-4">To use SubGuard, you must:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Be at least 18 years old</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized account access</li>
            </ul>
            <p className="text-gray-600 mt-4">
              You are responsible for all activities that occur under your account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Subscription Plans and Payments</h2>
            
            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">4.1 Available Plans</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Free Plan:</strong> Limited features, up to 5 subscriptions</li>
              <li><strong>Quick Check:</strong> One-time payment for a single 30-day email scan</li>
              <li><strong>Founding Guard:</strong> One-time payment for lifetime core features access</li>
              <li><strong>Monthly/Yearly Plans:</strong> Recurring subscription with full features</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">4.2 Payment Processing</h3>
            <p className="text-gray-600 mb-4">
              Payments are processed securely through Stripe. By providing payment information, you authorize us to charge the applicable fees.
            </p>

            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">4.3 Refunds</h3>
            <p className="text-gray-600">
              Due to the nature of our service, refunds are handled on a case-by-case basis. Please contact hello@subguardhq.com for refund requests within 14 days of purchase.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Gmail Integration</h2>
            <p className="text-gray-600 mb-4">
              When you connect your Gmail account to SubGuard:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>You grant us read-only access to scan for subscription-related emails</li>
              <li>We do not store the content of your emails, only extracted subscription metadata</li>
              <li>We cannot send, delete, or modify your emails</li>
              <li>You can disconnect your Gmail at any time from Settings</li>
            </ul>
            <p className="text-gray-600 mt-4">
              Our Gmail integration complies with Google's API Services User Data Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Acceptable Use</h2>
            <p className="text-gray-600 mb-4">You agree NOT to:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Upload malicious code or content</li>
              <li>Impersonate another person or entity</li>
              <li>Use the Service to spam or harass others</li>
              <li>Reverse engineer or attempt to extract source code</li>
              <li>Resell or redistribute the Service without permission</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-600 mb-4">
              The Service, including its design, features, content, and underlying technology, is owned by SubGuard and protected by intellectual property laws.
            </p>
            <p className="text-gray-600">
              You retain ownership of your data. By using the Service, you grant us a limited license to process your data as necessary to provide the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Disclaimers</h2>
            
            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">8.1 Service Accuracy</h3>
            <p className="text-gray-600 mb-4">
              While we strive to accurately detect and track subscriptions, we do not guarantee:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Detection of all subscriptions from your email</li>
              <li>Accuracy of detected amounts or renewal dates</li>
              <li>Success of cancellation instructions provided</li>
            </ul>
            <p className="text-gray-600 mt-4">
              You are responsible for verifying subscription information and completing cancellations directly with service providers.
            </p>

            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">8.2 No Financial Advice</h3>
            <p className="text-gray-600">
              SubGuard is a tracking tool, not a financial advisor. Any savings estimates or recommendations are for informational purposes only.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND</li>
              <li>WE ARE NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES</li>
              <li>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE PAST 12 MONTHS</li>
              <li>WE ARE NOT RESPONSIBLE FOR CHARGES FROM SUBSCRIPTIONS YOU FAILED TO CANCEL</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Indemnification</h2>
            <p className="text-gray-600">
              You agree to indemnify and hold harmless SubGuard, its officers, directors, employees, and agents from any claims, damages, or expenses arising from your use of the Service or violation of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Termination</h2>
            <p className="text-gray-600 mb-4">
              We may suspend or terminate your account if you violate these Terms. You may cancel your account at any time.
            </p>
            <p className="text-gray-600">
              Upon termination, your right to use the Service ceases immediately. We may retain certain data as required by law or for legitimate business purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Privacy</h2>
            <p className="text-gray-600">
              Your use of the Service is also governed by our <a href="/privacy" className="text-[#064E3B] underline">Privacy Policy</a>, which is incorporated into these Terms by reference.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Governing Law</h2>
            <p className="text-gray-600">
              These Terms shall be governed by and construed in accordance with applicable laws. Any disputes shall be resolved through binding arbitration or in the courts of competent jurisdiction.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">14. Changes to Terms</h2>
            <p className="text-gray-600">
              We reserve the right to modify these Terms at any time. We will notify users of material changes via email or through the Service. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">15. Contact Information</h2>
            <p className="text-gray-600">
              For questions about these Terms, please contact us at:
            </p>
            <p className="text-gray-900 font-medium mt-2">
              Email: hello@subguardhq.com
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm">© {new Date().getFullYear()} SubGuard. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
